/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { useForm, useGeolocation } from '@hooks';
import { renderSomethingWentWrong } from '@utils';
import { useErrorReporter } from '@lib/errorReporter';
import { useAuth } from '@lib/authentication';
import { useInsightsService } from '@lib/insightsService';
import { useProfile } from '@lib/profile';
import {
  ActionText,
  Button,
  Form,
  Modal,
  PasswordField,
  toast
} from '@components';
import {
  AuthenticationAttemptsLimitReached,
  DistanceExceededFromLastConnection,
  InvalidCredentials,
  InvestorHasAlreadyAnActiveSession,
  UnusualAccess
} from '@lib/hundredBricksAPI/authentication';

import {
  ModalTitle,
  ButtonContainer,
  LinksContainer,
  VPNErrorContainer,
  OptionContainer,
  ModalMessageContainer
} from './RenewSessionModal.style';
import RenewSessionMessage from './RenewSessionMessage';
import RenewSessionOptions from './RenewSessionOptions';

const passwordSchema = {
  password: {
    required: true
  }
};

const ERROR_INCORRECT_PASSWORD = 'La contraseña es inválida.';
const ERROR_LOCATION_BLOCKED = 'Ubicación desactivada. Activa los permisos de ubicación '
  + 'en tu navegador para acceder a tu cuenta.';

const RenewSessionModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const form = useForm({ initialValues: { password: '' }, schema: passwordSchema });
  const errorReporter = useErrorReporter();
  const { authenticationEvent, reportInsight } = useInsightsService();
  const { getCurrentPosition } = useGeolocation();
  const { profile, isLoading: isLoadingProfile } = useProfile();

  const {
    renewSession,
    error: loginError,
    isLoading: isAuthenticating,
    logout,
    clearSession,
    token
  } = useAuth();

  const email = profile.email.address;
  const tokenValue = useRef(token?.value).current;

  const onSubmit = (latitude, longitude) => {
    renewSession(email, form.values.password, latitude, longitude);
  };

  const handleSubmit = async () => {
    getCurrentPosition(coords => {
      const { latitude, longitude } = coords;

      onSubmit(latitude, longitude);
    }, () => {
      toast.danger(ERROR_LOCATION_BLOCKED);
    });
  };

  const handleForgotPassword = () => {
    clearSession();
    navigate('/forgot-password');
  };

  const handleUnusualAccessUnlock = () => {
    clearSession();
    navigate(`/unusual-access-unblock?email=${encodeURIComponent(email)}`);
  };

  const handleLoginError = error => {
    reportInsight(authenticationEvent.logInError, error);

    if (error instanceof InvalidCredentials) {
      form.addError({
        field: 'password',
        message: ERROR_INCORRECT_PASSWORD
      });
    } else {
      errorReporter.critical(error);
      renderSomethingWentWrong();
    }
  };

  const hasModalError = loginError instanceof AuthenticationAttemptsLimitReached
    || loginError instanceof InvestorHasAlreadyAnActiveSession
    || loginError instanceof UnusualAccess;

  useEffect(() => {
    if (token && tokenValue !== token.value) {
      onClose();
      form.resetValues();
    }
  }, [token]);

  useEffect(() => {
    if (loginError && !hasModalError) {
      handleLoginError(loginError);
    }
  }, [loginError]);

  return (
    <Modal isOpen={isOpen} preventClose>
      <ModalTitle>Por inactividad, tu sesión ha expirado</ModalTitle>

      {hasModalError
        ? (
          <ModalMessageContainer>
            <RenewSessionMessage error={loginError} />

            <OptionContainer>
              <RenewSessionOptions
                error={loginError}
                onConfirm={handleUnusualAccessUnlock}
                onCancel={logout}
              />
            </OptionContainer>
          </ModalMessageContainer>
        )
        : (
          <div>
            <Form onSubmit={form.handleSubmit(handleSubmit)}>
              <PasswordField
                id="password"
                focus
                label="Reactiva tu sesión para continuar explorando."
                placeholder="Escribe tu contraseña"
                value={form.values.password}
                {...form.fieldProps('password')}
              />

              {
                loginError instanceof DistanceExceededFromLastConnection && (
                  <VPNErrorContainer>
                    Tenemos un inconveniente al tratar de iniciar tu sesión.
                    ¿Tienes activa una VPN o un proxy?
                    Desactívala y vuelve a intentarlo.
                  </VPNErrorContainer>
                )
              }

              <ButtonContainer>
                <Button
                  type="submit"
                  isLoading={isAuthenticating || isLoadingProfile}
                >
                  Reactivar sesión
                </Button>
              </ButtonContainer>
            </Form>

            <LinksContainer>
              <ActionText
                color="info"
                onClick={handleForgotPassword}
                disabled={isAuthenticating || isLoadingProfile}
              >
                Restablece tu contraseña
              </ActionText>

              <ActionText
                color="info"
                onClick={logout}
                disabled={isAuthenticating || isLoadingProfile}
              >
                Cerrar sesión
              </ActionText>
            </LinksContainer>
          </div>
        )}
    </Modal>
  );
};

RenewSessionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default RenewSessionModal;
